import clsx from 'clsx'
import { useCioSearchResults } from './hooks/useCioSearchResults'
import { CioResultList } from './components/CioResultsList'
import { usePreContentQuery } from '@contentfulTypes'
import { createGraphQLClient } from '~/graphql/contentfulClient'
import { usePreviewMode } from '~/hooks/usePreviewMode'
import { useRouter } from 'next/router'
import { createLanguageRegionLocale } from '~/lib/createLanguageRegionLocale'
import { DEFAULT_LOCALE } from '~/config/constants'
import CmsPage from '../CmsPage/CmsPage'
import { mapProduct } from './lib/mapProduct'
import { useEffect } from 'react'
import { DataLayerEnum, useGTM } from '~/hooks/useGTM'
import { useTranslation } from 'next-i18next'
import { VariationsStrategy } from './lib/constants'
import { getMinimumPrice } from '~/lib/getMinimumPrice'

type CioSearchResultPageProps = {
  slug?: string
  filterName: string
  filterValue: string
  headline?: string | null
  showVariationsStrategy?: VariationsStrategy
}

export const CioBrowseResultPage = ({
  slug,
  filterName,
  filterValue,
  headline,
  showVariationsStrategy,
}: CioSearchResultPageProps) => {
  const { t } = useTranslation()
  const { dataLayer } = useGTM()
  const router = useRouter()
  const preview = usePreviewMode()
  const contentfulClient = createGraphQLClient({ preview })
  const finalLocale = router.locale ? createLanguageRegionLocale(router.locale) : DEFAULT_LOCALE
  const results = useCioSearchResults({
    type: 'browse',
    subject: { filterName, filterValue },
    enabled: true,
    sort: {},
  })
  const hasResults = results.data?.pages?.[0]?.response?.results?.length > 0

  const { data: headerContentData } = usePreContentQuery(contentfulClient, {
    slug: `header-${slug?.replace('enhancedsearch/', '')}`,
    preview,
    locale: finalLocale,
  })

  const { data: contentData } = usePreContentQuery(
    contentfulClient,
    {
      slug: slug || '',
      preview,
      locale: finalLocale,
    },
    {
      enabled: !!slug,
    },
  )

  useEffect(() => {
    const allResults = (results.data?.pages ?? []).flatMap((page) => page?.response?.results ?? [])

    if (allResults.length === 0) {
      return
    }

    const items =
      allResults.map((result) => {
        const configurableProduct = mapProduct(result)
        const minimumPrice = configurableProduct.price_range?.minimum_price

        return {
          item_id: configurableProduct.sku,
          item_name: configurableProduct.name,
          price: getMinimumPrice(minimumPrice)?.value,
        }
      }) || []

    const currency = results.data?.pages?.[0]?.response?.results?.[0]?.data?.currency_code || 'EUR'

    dataLayer(DataLayerEnum.EVENT, 'view_item_list', {
      ecommerce: {
        currency,
        items,
      },
    })
  }, [results.data?.pages, dataLayer])

  return (
    <article className="pt-[var(--header-padding)]">
      {headerContentData?.pageCollection?.items?.[0]?.contentModulesCollection?.items?.length ? (
        <div className="mb-8 lg:mb-16">
          <CmsPage
            slug={`header-${slug?.replace('enhancedsearch/', '')}`}
            disableBreadcrumb={true}
            disableLanguageAlternates={true}
            paddingTop={false}
          />
        </div>
      ) : (
        (headline || contentData?.pageCollection?.items[0]?.title) && (
          <h1 className="min-h-[56px] text-center text-40-45-sg font-extralight lgx:text-80-90-sg">
            {headline || contentData?.pageCollection?.items?.[0]?.title || ''}
          </h1>
        )
      )}
      <p
        className={clsx(
          'mt-2.5 text-center text-15-22-sg font-extralight text-sg-dark-grey lg:mt-5',
          results.isLoading && 'opacity-0',
        )}
      >
        {`${results.data?.pages?.[0]?.response?.total_num_results || 0} ${t('Products')}`}
      </p>
      <CioResultList results={results} showVariationsStrategy={showVariationsStrategy} />
      {slug && (
        <CmsPage
          slug={slug}
          disableBreadcrumb={true}
          disableLanguageAlternates={true}
          paddingTop={false}
        />
      )}
    </article>
  )
}

export default CioBrowseResultPage
